import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const NORTH_EAST_ARROW = "\u2197\uFE0E";

function Arrow({ direction = "right" }) {
  return (
    <span className={`arrow arrow-${direction}`} aria-hidden="true">
      {direction === "left" ? "←" : NORTH_EAST_ARROW}
    </span>
  );
}

export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className="site-header">
      <Link className="wordmark" to="/" aria-label="Clément Girault, home">
        <span className="wordmark-mark">CG</span>
        <span>
          Thinking Out
          <small>Clément Girault · Sydney</small>
        </span>
      </Link>
      <button
        className="menu-toggle"
        type="button"
        aria-expanded={menuOpen}
        aria-controls="primary-navigation"
        onClick={() => setMenuOpen((value) => !value)}
      >
        <span>{menuOpen ? "Close" : "Menu"}</span>
        <span className="menu-toggle-icon" aria-hidden="true">
          {menuOpen ? "×" : "☰"}
        </span>
      </button>
      <nav
        id="primary-navigation"
        className={menuOpen ? "primary-nav is-open" : "primary-nav"}
        aria-label="Primary navigation"
      >
        <NavLink exact to="/" activeClassName="is-active">
          Home
        </NavLink>
        <NavLink to="/work" activeClassName="is-active">
          Work
        </NavLink>
        <NavLink to="/about" activeClassName="is-active">
          Profile
        </NavLink>
        <a href="/cv" target="_blank" rel="noreferrer">
          CV
          <span className="sr-only"> (opens the CV viewer in a new tab)</span>
          <span className="nav-external-icon" aria-hidden="true">
            {NORTH_EAST_ARROW}
          </span>
        </a>
        <a href="mailto:clement.girault@icloud.com">
          Email me
          <span className="sr-only"> (opens your default email app)</span>
          <span className="nav-external-icon" aria-hidden="true">
            {NORTH_EAST_ARROW}
          </span>
        </a>
      </nav>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="site-footer" id="contact">
      <div className="footer-prompt">
        <span className="eyebrow">Have something interesting in mind?</span>
        <a
          href="mailto:clement.girault@icloud.com"
          aria-describedby="footer-email-note"
        >
          Email me to talk it through.
          <Arrow />
        </a>
        <span className="footer-email-note" id="footer-email-note">
          Opens your default email app.
        </span>
      </div>
      <div className="footer-grid">
        <div>
          <span className="footer-label">Write</span>
          <a href="mailto:clement.girault@icloud.com">
            clement.girault@icloud.com
          </a>
        </div>
        <div>
          <span className="footer-label">Talk</span>
          <a href="tel:+61431945020">+61 431 945 020</a>
        </div>
        <div>
          <span className="footer-label">Elsewhere</span>
          <a
            href="https://www.linkedin.com/in/clement-girault-australia"
            target="_blank"
            rel="noreferrer"
          >
            LinkedIn
          </a>
          <a
            href="https://github.com/interior-night"
            target="_blank"
            rel="noreferrer"
          >
            GitHub
          </a>
        </div>
        <div className="footer-signoff">
          <span>Clément Girault</span>
          <span>© {new Date().getFullYear()}</span>
        </div>
      </div>
    </footer>
  );
}

export function PageFrame({ children }) {
  return (
    <>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <SiteHeader />
      <main id="main-content">{children}</main>
      <SiteFooter />
    </>
  );
}

export function PageMeta({ title, description }) {
  useEffect(() => {
    document.title = title
      ? `${title} — Clément Girault`
      : "Clément Girault — Digital strategy, AI, image and sound";
    const meta = document.querySelector('meta[name="description"]');
    if (meta && description) meta.setAttribute("content", description);
  }, [description, title]);

  return null;
}

export function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  return null;
}

export { Arrow, NORTH_EAST_ARROW };
